import { Injectable, computed, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { UserSessionService } from './user-session.service';
import { AppRoles, PermissionResources, PermissionActions } from '#enums';
import type { RoleAssignment, UserScopeJson } from '#models';

@Injectable({ providedIn: 'root' })
export class PermissionService {
    private _authService = inject(AuthService);
    private _sessionService = inject(UserSessionService);

    /** All role assignments for the current user. Empty array until /auth/me resolves. */
    readonly assignments = computed(
        () => this._authService.userDetails()?.roles ?? []
    );

    private readonly _isActive = computed(
        () => this._authService.userDetails()?.isActive ?? false
    );

    /**
     * The currently active role assignment. Resolves from the API-sourced activeRoleId
     * when available, otherwise defaults to the first assignment.
     * Recomputes automatically when the user switches roles or logs in/out.
     *
     * Returns null when the user has explicitly reset to "All Roles" (FB-004) — an empty
     * string sentinel, distinct from a stored null which means "never chosen" and still
     * falls back to the first assignment. Permission/scope checks then OR across every
     * assignment via {@link assignments} instead of reading a single active one.
     */
    readonly activeAssignment = computed((): RoleAssignment | null => {
        const roles = this.assignments();
        if (roles.length === 0) return null;
        const apiRoleId = this._authService.userDetails()?.activeRoleId
            ?? this._sessionService.activeRoleId();
        if (apiRoleId === '') return null; // explicit "All Roles" reset
        if (apiRoleId) {
            const found = roles.find(r => r.roleId === apiRoleId);
            if (found) return found;
        }
        return roles[0];
    });

    // ── Derived helpers ────────────────────────────────────────────────────────

    /**
     * True when the active role has Admin > CanAccess.
     * Covers both ARES.ITAdmin and ARES.SuperUser.
     */
    readonly isAdmin = computed(() =>
        this.hasPermission(PermissionResources.Admin, PermissionActions.CanAccess)
    );

    /**
     * True only when the user authenticated via Okta AND holds ARES.ThirdParty
     * or ARES.ThirdPartyAdmin. Internal Entra users who happen to have ThirdParty
     * assigned for testing are intentionally excluded.
     */
    readonly isThirdPartyRole = computed(() => {
        const user = this._sessionService.currentUser();
        if (!user?.isExternal) return false;
        return user.roles.some(r => r.roleId === AppRoles.ThirdParty || r.roleId === AppRoles.ThirdPartyAdmin);
    });

    /**
     * Mirrors the API's vendor isolation: external users, and the active ThirdParty/ThirdPartyAdmin role,
     * only see their own vendor's remediations.
     */
    readonly isVendorRestricted = computed(() => {
        if (this._sessionService.currentUser()?.isExternal) return true;
        const role = this.activeAssignment()?.roleId;
        return role === AppRoles.ThirdParty || role === AppRoles.ThirdPartyAdmin;
    });

    /**
     * True when an external ThirdParty user has explicit project IDs in their scope.
     * scopes === null means "JIT default — pending admin assignment", not full access.
     * Returns false immediately for non-ThirdParty users.
     */
    readonly hasProjectAssignments = computed(() => {
        if (!this.isThirdPartyRole()) return false;
        const roles = this._sessionService.currentUser()?.roles ?? [];
        return roles
            .filter(r => r.roleId === AppRoles.ThirdParty || r.roleId === AppRoles.ThirdPartyAdmin)
            .some(r => Array.isArray(r.scopes?.projects) && r.scopes!.projects!.length > 0);
    });

    // ──────────────────────────────────────────────────────────────────────────

    /**
     * Checks whether the active role holds a specific permission,
     * optionally constrained to a data scope.
     *
     * In "All Roles" mode (no single active assignment — FB-004), this ORs the check
     * across every assignment: the permission is granted if ANY assigned role individually
     * grants it. Scopes are never merged across roles (that would incorrectly change their
     * internal AND semantics into an unintended combination) — each assignment is evaluated
     * independently.
     *
     * @param resource  The resource being checked (e.g. 'Assets', 'Projects').
     * @param action    The action being checked   (e.g. 'CanView', 'CanEdit').
     * @param scope     Optional single-entry map  { ScopeType: ScopeValue }.
     * @returns true if the active role assignment (or any assignment in All-Roles mode) grants the permission.
     */
    hasPermission(
        resource: string,
        action: string,
        scope?: Record<string, string>
    ): boolean {
        if (!this._isActive()) return false;

        const scopeEntry = scope ? Object.entries(scope)[0] : undefined;
        const [checkType, checkValue] = scopeEntry ?? [undefined, undefined];

        const active = this.activeAssignment();
        const candidates = active ? [active] : this.assignments();

        return candidates.some(a => {
            // null scopes = explicitly-granted full access — matches every scope check
            const scopeMatches =
                a.scopes === null ||
                checkType === undefined ||
                this._scopeMatchesField(a.scopes, checkType, checkValue!);

            if (!scopeMatches) return false;

            // '*' wildcard covers all resources (used by ARES.SuperUser)
            const wildcard = a.permissions['*'] ?? [];
            const specific = a.permissions[resource] ?? [];
            return wildcard.includes(action) || specific.includes(action);
        });
    }

    /**
     * Returns the hierarchy nodes the user can access based on the active role's scopes,
     * or — in "All Roles" mode — the union across every assignment.
     * Returns hasFullAccess=true when any candidate assignment has no scope restrictions.
     */
    getUserHierarchyFilter(): { hasFullAccess: boolean; scopeValues: Map<string, Set<string>> } {
        const active = this.activeAssignment();
        const candidates = active ? [active] : this.assignments();
        if (candidates.length === 0 || candidates.some(a => a.scopes === null)) {
            return { hasFullAccess: true, scopeValues: new Map() };
        }

        const scopeValues = new Map<string, Set<string>>();
        for (const a of candidates) {
            for (const [key, val] of Object.entries(a.scopes!)) {
                if (val === null || val === undefined) continue;
                if (!scopeValues.has(key)) scopeValues.set(key, new Set());
                const set = scopeValues.get(key)!;
                if (typeof val === 'string') set.add(val);
                if (Array.isArray(val)) val.forEach(v => set.add(v));
            }
        }
        return { hasFullAccess: false, scopeValues };
    }

    /** Returns true when the scope object contains the given type/value pair. */
    private _scopeMatchesField(scopes: UserScopeJson, type: string, value: string): boolean {
        const key = (type.charAt(0).toLowerCase() + type.slice(1)) as keyof UserScopeJson;
        const fieldVal = scopes[key];
        if (Array.isArray(fieldVal)) return fieldVal.includes(value);
        return typeof fieldVal === 'string' && fieldVal === value;
    }
}
